// Street View pegman for the web maps: the pure half.
//
// Dragging the pegman out of MapControls paints Google's Street View coverage
// (the blue lines) over the Mapbox map, and dropping it opens a Street View
// panorama at the nearest imagery. Everything here is policy with no DOM,
// Mapbox or Google dependency so it can be unit-tested; the side-effecting
// half lives in streetViewGoogle.ts and the UI in StreetViewControl.vue.
//
// The coverage comes from the Map Tiles API's 2D tiles, not the Maps JS
// StreetViewCoverageLayer — that layer only renders on a google.maps.Map, and
// this map is Mapbox. A session asked for `layerStreetview` with
// `overlay: true` returns just the blue lines on a transparent background,
// which Mapbox can draw as an ordinary raster source.

export const COVERAGE_SOURCE_ID = 'hiiker-street-view-coverage';
export const COVERAGE_LAYER_ID = 'hiiker-street-view-coverage';

const MAP_TILES_BASE = 'https://tile.googleapis.com/v1';

/** Pixels the pointer must travel before a press on the pegman counts as a drag rather than a click. */
export const DRAG_THRESHOLD_PX = 4;

/**
 * Sessions last about two weeks; renew well before the server says so, so a
 * tab left open never starts requesting tiles with a token that just lapsed.
 */
export const SESSION_RENEW_MARGIN_MS = 60 * 60 * 1000;

export interface CoverageSession {
  token: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export function createSessionRequest(apiKey: string): { url: string; body: Record<string, unknown> } {
  return {
    url: `${MAP_TILES_BASE}/createSession?key=${encodeURIComponent(apiKey)}`,
    body: {
      mapType: 'roadmap',
      language: 'en-US',
      region: 'US',
      layerTypes: ['layerStreetview'],
      overlay: true,
    },
  };
}

/**
 * The createSession response, or null for anything that isn't one — an error
 * body (a key without the Map Tiles API enabled answers 403 with JSON) must
 * not be cached as if it were a session.
 */
export function parseSessionResponse(json: unknown): CoverageSession | null {
  if (!json || typeof json !== 'object') return null;
  const { session, expiry } = json as { session?: unknown; expiry?: unknown };
  if (typeof session !== 'string' || session.length === 0) return null;

  // `expiry` is epoch seconds, sent as a string.
  const expirySeconds = Number(expiry);
  if (!Number.isFinite(expirySeconds) || expirySeconds <= 0) return null;

  return { token: session, expiresAt: expirySeconds * 1000 };
}

export function isSessionFresh(session: CoverageSession | null, now: number): boolean {
  return Boolean(session) && session.expiresAt - SESSION_RENEW_MARGIN_MS > now;
}

export function coverageTileUrl(sessionToken: string, apiKey: string): string {
  return `${MAP_TILES_BASE}/2dtiles/{z}/{x}/{y}?session=${encodeURIComponent(sessionToken)}&key=${encodeURIComponent(apiKey)}`;
}

/**
 * How far from the drop point to look for a panorama.
 *
 * A drop is only as precise as the pegman's feet on screen, and a screen pixel
 * covers far more ground zoomed out than in. Searching a fixed 50m (Google's
 * default) makes a drop that visibly lands on a blue line miss whenever the
 * map is zoomed out, so the radius scales with the ground a few pixels cover.
 * Clamped: never tighter than Google's default, never so wide that a drop in
 * the hills opens a panorama in the next town.
 *
 * Mapbox GL uses 512px tiles, hence 78271.517m per pixel at zoom 0 on the equator.
 */
export const MIN_SEARCH_RADIUS_M = 50;
export const MAX_SEARCH_RADIUS_M = 2000;
const DROP_TOLERANCE_PX = 16;

export function dropSearchRadiusMeters(latitude: number, zoom: number): number {
  const metersPerPixel = (78271.517 * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoom;
  const radius = metersPerPixel * DROP_TOLERANCE_PX;
  if (!Number.isFinite(radius)) return MIN_SEARCH_RADIUS_M;
  return Math.round(Math.min(MAX_SEARCH_RADIUS_M, Math.max(MIN_SEARCH_RADIUS_M, radius)));
}

export interface Point { x: number; y: number }
export interface Rect { left: number; top: number; right: number; bottom: number }

export function hasPassedDragThreshold(start: Point, current: Point): boolean {
  return Math.hypot(current.x - start.x, current.y - start.y) >= DRAG_THRESHOLD_PX;
}

/** The drop point in the map canvas's own pixels, or null when it was dropped off the map. */
export function dropPointOnMap(client: Point, canvas: Rect): Point | null {
  const inside = client.x >= canvas.left && client.x <= canvas.right
    && client.y >= canvas.top && client.y <= canvas.bottom;
  return inside ? { x: client.x - canvas.left, y: client.y - canvas.top } : null;
}

/** Street View headings are 0–360 clockwise from north; Mapbox bearings run -180–180. */
export function headingFromBearing(bearing: number): number {
  if (!Number.isFinite(bearing)) return 0;
  return ((bearing % 360) + 360) % 360;
}

/**
 * Street View is a PRO+ feature (each opened panorama is a billed Google load).
 *
 * Everyone else gets one free look: their first drag shows the coverage lines
 * as a taste, and the drop opens the PRO+ dialog instead of a panorama. After
 * that, pressing the pegman goes straight to the dialog.
 *
 * `isProPlus` is null while RevenueCat is still answering; the press waits for
 * it rather than letting a drag start that would then be cut off.
 */
export const STREET_VIEW_UPGRADE_URL = '/purchases';
export const STREET_VIEW_PREVIEW_STORAGE_KEY = 'hiiker:streetViewPreviewUsed';

export type StreetViewAccess = 'allowed' | 'preview' | 'paywall' | 'pending';

export function streetViewAccess(isProPlus: boolean | null, previewUsed: boolean): StreetViewAccess {
  if (isProPlus === null) return 'pending';
  if (isProPlus) return 'allowed';
  return previewUsed ? 'paywall' : 'preview';
}

/**
 * One-off "New: Street View" hint under the pegman, for people who were already
 * HiiKER users when the feature launched — new sign-ups meet it through normal
 * onboarding instead. Shown on their first map visit after launch, then never
 * again on that browser (it's marked seen as soon as it appears, so ignoring it
 * counts).
 */
export const STREET_VIEW_LAUNCHED_AT = Date.parse('2026-10-12T00:00:00Z');
export const STREET_VIEW_HINT_STORAGE_PREFIX = 'hiiker:streetViewHintSeen:';

export function streetViewHintStorageKey(userId: string): string {
  return `${STREET_VIEW_HINT_STORAGE_PREFIX}${userId}`;
}

export function shouldShowStreetViewHint(
  user: { firebase_user_id?: string | null; created_at?: string | null } | null | undefined,
  alreadySeen: boolean,
  launchedAt: number = STREET_VIEW_LAUNCHED_AT,
): boolean {
  if (alreadySeen || !user?.firebase_user_id || !user.created_at) return false;
  const createdAt = Date.parse(user.created_at);
  return Number.isFinite(createdAt) && createdAt < launchedAt;
}
